import { z } from 'zod';
import { ulid } from 'ulid';

import type {
        CreateLimitPolicyInput,
        UpdateLimitPolicyInput
} from '@/graphql/generated';

const nonEmptyString = (field: string) =>
        z
                .string({ required_error: `${field} is required` })
                .trim()
                .min(1, `${field} is required`);

const ULID_REGEX = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/i;

const optionalUlidString = z.union([
        z.literal(''),
        z
                .string()
                .trim()
                .regex(ULID_REGEX, 'Value must be a valid ULID.')
]);

export const limitPolicyFormSchema = z.object({
        name: nonEmptyString('Name'),
        priority: z
                .string({ required_error: 'Priority is required' })
                .trim()
                .regex(/^[0-9]+$/, 'Priority must be a whole number'),
        isActive: z.boolean(),
        scenarioId: optionalUlidString,
        limitId: nonEmptyString('Limit'),
        billingCycleId: nonEmptyString('Billing cycle')
});

export type LimitPolicyFormValues = z.infer<typeof limitPolicyFormSchema>;

const parseRequiredInteger = (value: string, fieldName: string): number => {
        const parsed = Number.parseInt(value, 10);

        if (Number.isNaN(parsed)) {
                throw new Error(`${fieldName} is invalid.`);
        }

        return parsed;
};

const sanitiseName = (value: string) => value.trim();

const normaliseScenarioId = (value: string | undefined) => {
        if (!value) {
                return undefined;
        }

        return value.trim() === '' ? undefined : value.trim();
};

export const buildCreateLimitPolicyInput = (
        values: LimitPolicyFormValues,
        limitPolicyId: string
): CreateLimitPolicyInput => ({
        limitPolicyId,
        name: sanitiseName(values.name),
        priority: parseRequiredInteger(values.priority, 'Priority'),
        isActive: values.isActive,
        scenarioId: normaliseScenarioId(values.scenarioId),
        limitId: values.limitId,
        billingCycleId: parseRequiredInteger(
                values.billingCycleId,
                'Billing cycle'
        )
});

export const buildUpdateLimitPolicyInput = (
        values: LimitPolicyFormValues,
        limitPolicyId: string
): UpdateLimitPolicyInput => ({
        limitPolicyId,
        name: sanitiseName(values.name),
        priority: parseRequiredInteger(values.priority, 'Priority'),
        isActive: values.isActive,
        scenarioId: normaliseScenarioId(values.scenarioId),
        limitId: values.limitId,
        billingCycleId: parseRequiredInteger(
                values.billingCycleId,
                'Billing cycle'
        )
});

export const generateIdempotentLimitPolicyId = (): string => {
        return ulid();
};
